/**
 * Controle da opção "Manter conectado" no login administrativo.
 *
 * Quando o usuário desmarca a opção, a sessão do Supabase deve expirar ao
 * fechar o navegador (ou seja, não sobreviver entre "sessões" do browser).
 *
 * O client do Supabase é criado com `storage: localStorage` (auto-gerado,
 * não podemos alterar). Para simular o comportamento de sessionStorage sem
 * mexer no client, marcamos a sessão como "efêmera" em localStorage e
 * usamos sessionStorage como sinal de "aba/sessão de browser ainda viva".
 * No boot, se o flag efêmero existe MAS o marcador de sessionStorage não
 * (ou seja, o browser foi fechado e reaberto), removemos os tokens do
 * Supabase ANTES do client hidratar, forçando novo login.
 */

const EPHEMERAL_FLAG_KEY = "zappy.auth.ephemeral";
const TAB_ALIVE_KEY = "zappy.auth.tab-alive";

const safeLocal = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const safeSession = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

/** Marca ou limpa a sessão atual como efêmera (expira ao fechar o navegador). */
export const setSessionEphemeral = (ephemeral: boolean): void => {
  const local = safeLocal();
  const session = safeSession();
  if (!local || !session) return;
  try {
    if (ephemeral) {
      local.setItem(EPHEMERAL_FLAG_KEY, "1");
      session.setItem(TAB_ALIVE_KEY, "1");
    } else {
      local.removeItem(EPHEMERAL_FLAG_KEY);
      session.removeItem(TAB_ALIVE_KEY);
    }
  } catch {
    /* storage indisponível */
  }
};

/**
 * Executa no boot ANTES de qualquer import do supabase client.
 * Se a última sessão foi marcada como efêmera e o browser foi fechado
 * (sessionStorage limpo), remove os tokens persistidos do Supabase.
 */
export const enforceEphemeralSessionBoot = (): void => {
  const local = safeLocal();
  const session = safeSession();
  if (!local || !session) return;
  try {
    const isEphemeral = local.getItem(EPHEMERAL_FLAG_KEY) === "1";
    if (!isEphemeral) return;
    const tabAlive = session.getItem(TAB_ALIVE_KEY) === "1";
    if (tabAlive) return; // mesma "sessão de browser", mantém tokens

    // Nova sessão de browser após efêmera → limpa tokens do Supabase.
    const toRemove: string[] = [];
    for (let i = 0; i < local.length; i++) {
      const k = local.key(i);
      if (k && /^sb-.+-auth-token$/.test(k)) toRemove.push(k);
    }
    toRemove.forEach((k) => local.removeItem(k));
    local.removeItem(EPHEMERAL_FLAG_KEY);
  } catch {
    /* ignore */
  }
};
